/**
 * FakeBSOD.jsx - Fake Purple Screen of Death for Null Pointer aircraft
 *
 * A full-screen overlay that mimics a BSOD but in PURPLE (Mythic tease).
 * Click anywhere to dismiss. The game continues normally after dismissal.
 */

import React, { useEffect, useCallback } from "react";

export default function FakeBSOD({ isOpen, onClose, text }) {
  // Handle click to dismiss
  const handleClick = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Handle escape key to dismiss
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const defaultText = `AIRLINE_OPS.exe has encountered a NULL_POINTER_EXCEPTION in flight.dll

The aircraft referenced memory at 0x00000000.

Click anywhere to continue.`;

  return (
    <div
      onClick={handleClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#5B21B6",
        color: "white",
        fontFamily: "'Segoe UI', monospace",
        fontSize: "18px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 99999,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {/* Sad face */}
      <div style={{ fontSize: "120px", marginBottom: "30px" }}>:(</div>

      {/* Error text */}
      <div
        style={{
          maxWidth: "700px",
          textAlign: "left",
          whiteSpace: "pre-wrap",
          lineHeight: 1.6,
          padding: "0 20px",
        }}
      >
        {text || defaultText}
      </div>

      {/* Reassurance - bright red so they KNOW it's fake */}
      <div
        style={{
          marginTop: "50px",
          fontSize: "16px",
          fontWeight: "bold",
          color: "#ff4444",
          textShadow: "0 0 10px rgba(255, 68, 68, 0.5)",
        }}
      >
        YOUR COMPUTER IS FINE. THIS IS FAKE.
      </div>
      <div
        style={{
          marginTop: "10px",
          fontSize: "14px",
          opacity: 0.7,
        }}
      >
        (Click anywhere to continue)
      </div>

      {/* Fake progress */}
      <div
        style={{
          marginTop: "30px",
          fontSize: "12px",
          opacity: 0.5,
        }}
      >
        0% complete
      </div>
    </div>
  );
}

/**
 * Hook to manage BSOD state
 */
export function useFakeBSOD() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [text, setText] = React.useState("");

  const showBSOD = useCallback((customText) => {
    setText(customText || "");
    setIsOpen(true);
  }, []);

  const hideBSOD = useCallback(() => {
    setIsOpen(false);
    setText("");
  }, []);

  return {
    isOpen,
    text,
    showBSOD,
    hideBSOD,
  };
}
